// Gift with purchase (launch preview, 2026-10-30).
//
// Keeps exactly one gift line in the cart while the cart qualifies, and none
// while it doesn't. Settings come from Theme settings > Launch promotions,
// rendered into #gwp-config by layout/theme.liquid.
//
// A cart qualifies when it has at least one line that is not the gift and not
// a FEE product, and the subtotal of those non-gift lines is at or above the
// minimum. FEE lines (setup fees, surcharges) are added by the logo
// customizer alongside a customized product, and a cart of fees alone is not
// an order.
//
// The gift is the only line this file ever adds, changes or removes, always
// by line KEY: it never touches a customized line or its fees. It is marked
// with the private property _gwp, which is what the cart templates key on to
// hide its quantity controls and what snippets/cart-order-details.liquid
// skips when choosing the line that carries the order-level properties.
//
// Triggers: page load, PUB_SUB_EVENTS.cartUpdate (published by Dawn's
// product form and cart, and by the logo customizer after its own direct
// /cart/add.js calls), and a back/forward cache restore. Its own writes
// publish with source 'gwp' so they are not re-handled.
(() => {
  const configEl = document.getElementById('gwp-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[gwp] bad config', e);
    return;
  }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const giftVariantId = Number(config.variantId) || null;
  const minimumCents = Math.round((Number(config.minimum) || 0) * 100);
  const SOURCE = 'gwp';
  // A variant the cart refused (not in this buyer's B2B catalog, a quantity
  // rule, out of stock) is not retried on every cart change during this page
  // view; a reload tries again.
  const refusedVariants = new Set();

  const isGiftLine = (item) => {
    const flag = item.properties && item.properties._gwp;
    return flag === true || flag === 'true';
  };
  const isFeeLine = (item) => String(item.vendor || '').trim().toUpperCase() === 'FEE';

  function qualifies(cart) {
    const paidLines = cart.items.filter((item) => !isGiftLine(item));
    if (!paidLines.some((item) => !isFeeLine(item))) return false;
    const subtotal = paidLines.reduce((sum, item) => sum + item.final_line_price, 0);
    return subtotal >= minimumCents;
  }

  async function cartRequest(path, body) {
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || `HTTP ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return data;
  }

  // Returns true when it changed the cart.
  async function reconcileOnce() {
    const cart = await cartRequest('cart.js');
    const giftLines = cart.items.filter(isGiftLine);
    const wanted = Boolean(config.enabled && giftVariantId && qualifies(cart));
    const keep = wanted ? giftLines.find((item) => item.variant_id === giftVariantId) : null;
    let changed = false;

    for (const line of giftLines) {
      if (line === keep) continue;
      await cartRequest('cart/change.js', { id: line.key, quantity: 0 });
      changed = true;
    }

    if (keep && keep.quantity !== 1) {
      await cartRequest('cart/change.js', { id: keep.key, quantity: 1 });
      changed = true;
    }

    if (wanted && !keep && !refusedVariants.has(giftVariantId)) {
      try {
        await cartRequest('cart/add.js', {
          items: [{ id: giftVariantId, quantity: 1, properties: { _gwp: 'true' } }],
        });
        changed = true;
      } catch (error) {
        refusedVariants.add(giftVariantId);
        console.warn('[gwp] the cart refused the gift variant', giftVariantId, error.message);
      }
    }

    return changed;
  }

  async function refreshCartUi() {
    if (typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // cart-items / cart-drawer-items re-render themselves on this.
      window.publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
    }

    const sections = ['cart-icon-bubble'];
    // The cart page footer (subtotal, order details) is a template section,
    // so it is fetched by its real section id, as Dawn's cart.js does.
    const pageFooter = document.getElementById('main-cart-footer');
    const footerSectionId = pageFooter && pageFooter.dataset.id;
    if (footerSectionId) sections.push(footerSectionId);

    try {
      const response = await fetch(`${root}cart?sections=${sections.join(',')}`);
      const rendered = await response.json();
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && rendered['cart-icon-bubble']) {
        const source = parse(rendered['cart-icon-bubble']).querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      }

      if (footerSectionId && rendered[footerSectionId]) {
        const source = parse(rendered[footerSectionId]).querySelector('.js-contents');
        const target = pageFooter.querySelector('.js-contents');
        if (source && target) target.innerHTML = source.innerHTML;
      }
    } catch (error) {
      console.error('[gwp] cart refresh failed', error);
    }
  }

  // One reconcile at a time; a trigger that lands mid-run schedules exactly
  // one more pass, so the gift can never be added twice by overlapping runs.
  let running = null;
  let rerun = false;

  function reconcile() {
    if (running) {
      rerun = true;
      return running;
    }
    running = (async () => {
      let changed = false;
      do {
        rerun = false;
        try {
          if (await reconcileOnce()) changed = true;
        } catch (error) {
          console.error('[gwp] reconcile failed', error);
        }
      } while (rerun);
      if (changed) await refreshCartUi();
    })().finally(() => {
      running = null;
    });
    return running;
  }

  window.SeamusGwp = { reconcile, whenIdle: () => running || Promise.resolve() };

  // The logo customizer adds a product and then its fee lines in separate
  // requests and publishes once at the end; a short debounce also folds
  // bursts of quantity changes into one pass.
  let timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      reconcile();
    }, 300);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === SOURCE) return;
      schedule();
    });
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule();
  });

  // Nothing to do on an empty cart unless a gift is somehow left behind,
  // which an empty cart cannot hold.
  if (config.itemCount > 0) reconcile();

  // Checkout/submit must not race a gift write (same 409 hazard the
  // cart-order-details submit guard in assets/cart.js handles). Capture
  // phase, so this runs before that guard; stopping propagation keeps the
  // guard from also acting on this event, and it sees the re-submitted one.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id !== 'cart' && form.id !== 'CartDrawer-Form') return;
      if (!running && !timer) return;

      event.preventDefault();
      event.stopPropagation();
      clearTimeout(timer);
      timer = null;
      reconcile().then(() => {
        if (form.requestSubmit) form.requestSubmit(event.submitter);
        else form.submit();
      });
    },
    true
  );
})();
